import React, { useEffect, useState, useMemo, useCallback, lazy, Suspense } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAppSelector, useAppDispatch } from '../../store'
import { getSubscriptionStatus, setShowExceededModal } from '../../store/slices/quotaSlice'
import { useAuthStore } from '../../auth/store'
import { getNotesCountFromCollection, isCollectionReady, onCollectionReady } from '../../store/collections'
import { subscriptionService } from '../../services/SubscriptionService'
import { getStorageUsedEstimateBytes, getStorageLimit } from '../../services/storageQuota'
import {
  onStorageUsageChanged,
  startStorageUsageAutoSync,
} from '../../services/assetCloud'
import {
  getDbQuotaState,
  getDbQuotaStateSync,
  setDbQuotaPlan,
  subscribeDbQuota,
  type DbQuotaState,
} from '../../services/dbQuota'
import OverlayModal from '../common/OverlayModal'

// 懒加载管理器：连带 antd Table 等重型组件移出首屏 bundle，打开弹窗时才加载
const AttachmentManager = lazy(() => import('../AttachmentManager/AttachmentManager'))
const ImageManager = lazy(() => import('../ImageManager/ImageManager'))

interface QuotaDisplayProps {
  compact?: boolean
}

function formatStorageSize(bytes: number): string {
  if (bytes === 0) return '0 B'
  const k = 1024
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
}

const QuotaDisplay: React.FC<QuotaDisplayProps> = ({ compact = false }) => {
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const { isAuthenticated, user, accessToken } = useAuthStore()
  const { currentCount, limit, remaining, planName, planResolved, subscriptionExpiresAt, isLoading } = useAppSelector((state) => state.quota)

  const [localCount, setLocalCount] = useState(0)
  const [notesLimit, setNotesLimit] = useState<number>(5)
  const [showAttachmentManager, setShowAttachmentManager] = useState(false)
  const [showImageManager, setShowImageManager] = useState(false)
  // ★ 数据库容量状态：事件驱动去抖刷新（collections 写钩子通知）+ 60s 兜底轮询，
  // 本地零网络估算，不增加服务端压力
  const [dbQuota, setDbQuota] = useState<DbQuotaState>(() => getDbQuotaStateSync())

  useEffect(() => {
    setDbQuota(getDbQuotaState())
    const unsubscribe = subscribeDbQuota(() => setDbQuota(getDbQuotaStateSync()))
    // 兜底：其他设备同步写入（本地钩子不触发）也能在 60s 内刷新
    const interval = setInterval(() => setDbQuota(getDbQuotaState()), 60_000)
    return () => {
      unsubscribe()
      clearInterval(interval)
    }
  }, [])

  // ★ 套餐注入：quotaSlice 的 planName 权威解析后（planResolved）才同步限额
  //   （免费 0.5M / 会员 20M）。★ 修复（2026-09-30）：此前无条件注入 initialState
  //   默认值 'free'——启动早期订阅未返回时会员被按 0.5M 误判超限，弹出
  //   "数据库总空间已满（0.5M）"错误 toast，订阅返回后又自动消失。
  //   未解析时注入 null → dbQuota 按会员 20M 宽松（服务端 pg_* 为权威兜底），
  //   解析后再收紧到真实套餐（真超限此时才提示，一次性 toast 不受影响）。
  useEffect(() => {
    setDbQuotaPlan(planResolved ? planName : null)
  }, [planName, planResolved])

  useEffect(() => {
    if (isAuthenticated && user && accessToken) {
      dispatch(getSubscriptionStatus())
    }
  }, [isAuthenticated, user, accessToken, dispatch])

  // 组件级 fetchCount：供初始加载与"订阅窗口关闭后刷新"共用
  const fetchCount = useCallback(async () => {
    if (user) {
      try {
        if (!isCollectionReady()) {
          console.log('[QuotaDisplay] 等待集合准备就绪...')
          await new Promise<void>((resolve) => {
            const unsub = onCollectionReady(() => {
              unsub()
              resolve()
            })
            setTimeout(resolve, 5000)
          })
        }

        console.log('[QuotaDisplay] 从 Electric SQL 集合获取笔记数量')
        const count = await getNotesCountFromCollection()
        console.log('[QuotaDisplay] 从集合获取笔记数量:', count)
        setLocalCount(count)

        const planLimit = await subscriptionService.getNotesLimit()
        console.log('[QuotaDisplay] 套餐笔记限额:', planLimit, 'planName:', planName)
        setNotesLimit(planLimit)
      } catch (error) {
        // Local-First：读取仅使用本地缓存数据，不回退到 PostgreSQL 直连
        console.warn('[QuotaDisplay] 集合获取失败，保持 0 计数:', error)
      }
    } else {
      console.warn('[QuotaDisplay] 用户未登录，无法获取笔记数量')
    }
  }, [user, planName])

  useEffect(() => {
    fetchCount()
  }, [fetchCount])

  const handleUpgrade = () => {
    // ★ 先进入项目自带套餐页：展示当前套餐状态与可升级选项，
    // 用户在套餐卡片内再点"升级"进入 Casdoor 自建支付页（应用内完成支付）
    navigate('/plans')
  }

  const { remainingDays, expiresAtFormatted } = useMemo(() => {
    if (!subscriptionExpiresAt || planName === 'free') {
      return { remainingDays: null, expiresAtFormatted: null }
    }
    
    const expiresAt = new Date(subscriptionExpiresAt)
    const now = new Date()
    const diffTime = expiresAt.getTime() - now.getTime()
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
    
    const formatted = expiresAt.toLocaleDateString('zh-CN', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    })
    
    return { remainingDays: diffDays, expiresAtFormatted: formatted }
  }, [subscriptionExpiresAt, planName])

  const [storageData, setStorageData] = useState(() => {
    // ★ 空间统计：缓存优先（含其他设备上传、零网络），无缓存回退本地索引。
    // 不在启动时发起任何网络查询；上传/删除后缓存增量更新，统计自然准确。
    const used = getStorageUsedEstimateBytes()
    const limit = getStorageLimit(planName)
    const pct = limit > 0 ? (used / limit) * 100 : 0
    return { used, limit, percentage: pct }
  })

  const refreshStorage = useCallback(() => {
    const used = getStorageUsedEstimateBytes()
    const limit = getStorageLimit(planName)
    const pct = limit > 0 ? (used / limit) * 100 : 0
    setStorageData({ used, limit, percentage: pct })
  }, [planName])

  useEffect(() => {
    refreshStorage()
  }, [refreshStorage])

  // ★ 空间占用变更自动刷新：上传/删除/打开管理页刷新 OSS 缓存后，
  // 侧边栏统计立即同步（读缓存零网络），无需高频轮询。
  useEffect(() => {
    const unsub = onStorageUsageChanged(() => refreshStorage())
    return unsub
  }, [refreshStorage])

  // ★ 空间占用低频自动校准（短周期体现其他设备变化 + 不高频读写服务器）：
  // 每 5 分钟一次数据库 SUM（单条 SQL 毫秒级），本地缓存 TTL 内零网络秒开；
  // 组件卸载（侧边栏关闭/退出登录）即停止，不常驻后台。
  useEffect(() => {
    const stopAutoSync = startStorageUsageAutoSync()
    return stopAutoSync
  }, [])

  const storageIsNearLimit = storageData.percentage >= 80 && storageData.percentage < 100

  const getStorageProgressBarColor = (): string => {
    if (storageData.percentage >= 100) return 'bg-red-500'
    if (storageIsNearLimit) return 'bg-amber-500'
    return 'bg-emerald-500'
  }

  if (!isAuthenticated) {
    return null
  }

  const displayCount = localCount > 0 ? localCount : currentCount
  // ★ 修复（2026-09-30）：条数上限优先取权威解析（服务端/Rust get_subscription_status）
  //   写入 redux 的 limit。此前 `notesLimit || limit` 让本地 notesLimit 永远遮蔽 redux——
  //   而本地值来自前端直连 Casdoor 的 getNotesLimit()（客户端 secret 移除后订阅查询 401
  //   降级 userinfo 兜底，失败回退 free=5），导致会员显示 25/5 并误报"已达上限"。
  //   notesLimit 仅在套餐未解析时作显示兜底。
  const effectiveLimit = planResolved ? limit : (notesLimit || limit)
  const percentage = effectiveLimit > 0 ? (displayCount / effectiveLimit) * 100 : 0
  // ★ 未解析（planResolved=false）不判"已达上限/接近上限"——与 dbQuota 宽松语义一致，
  //   实际创建不受影响（服务端权威校验兜底），不得凭默认值宣称超限
  const isExceeded = planResolved && displayCount >= effectiveLimit
  const isNearLimit = planResolved && percentage >= 80 && !isExceeded
  const isExpired = remainingDays !== null && remainingDays <= 0

  const getPlanBadgeStyle = (planName: string): string => {
    const styles: Record<string, string> = {
      'free': 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300',
      'vip': 'bg-blue-100 text-blue-700 dark:bg-blue-700 dark:text-blue-300',
      'super-vip': 'bg-gradient-to-r from-amber-400 to-orange-500 text-white',
    }
    return styles[planName] || styles['free']
  }

  const getPlanDisplayName = (planName: string): string => {
    const names: Record<string, string> = {
      'free': '免费版',
      'vip': '畅用会员',
      'super-vip': '畅存会员',
    }
    return names[planName] || '免费版'
  }

  const getProgressBarColor = (): string => {
    if (isExceeded) return 'bg-red-500'
    if (isNearLimit) return 'bg-yellow-500'
    return 'bg-blue-500'
  }

  if (compact) {
    return (
      <div 
        className="flex items-center space-x-2 cursor-pointer hover:opacity-80 transition-opacity"
        onClick={handleUpgrade}
      >
        <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${getPlanBadgeStyle(planName)}`}>
          {getPlanDisplayName(planName)}
        </span>
        <span className="text-xs text-gray-500 dark:text-gray-400">
          {displayCount}/{effectiveLimit}
        </span>
        {isExceeded && (
          <span className="text-xs text-red-500">已满</span>
        )}
      </div>
    )
  }

  return (
    <div className="p-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
      <div className="flex items-center justify-between mb-2">
        <span className={`px-2 py-1 text-sm font-medium rounded-full ${getPlanBadgeStyle(planName)}`}>
          {getPlanDisplayName(planName)}
        </span>
        {(planName !== 'super-vip' || isExpired) && (
          <button
            onClick={handleUpgrade}
            className="text-xs text-blue-600 hover:text-blue-700 dark:text-blue-400 font-medium transition-colors"
          >
            {isExpired ? '续费' : '升级'}
          </button>
        )}
      </div>

      {planName !== 'free' && remainingDays !== null && expiresAtFormatted && (
        <div className={`mb-2 p-2 rounded text-xs ${
          isExpired 
            ? 'bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400' 
            : remainingDays <= 7 
              ? 'bg-yellow-50 dark:bg-yellow-900/20 text-yellow-600 dark:text-yellow-400'
              : 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400'
        }`}>
          {isExpired ? (
            <span>会员已过期，请续费</span>
          ) : (
            <span>
              有效期至 {expiresAtFormatted} (剩余 {remainingDays} 天)
            </span>
          )}
        </div>
      )}

      <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2 mb-2">
        <div
          className={`h-2 rounded-full transition-all ${getProgressBarColor()}`}
          style={{ width: `${Math.min(100, percentage)}%` }}
        ></div>
      </div>

      <div className="flex items-center justify-between text-xs">
        <span className="text-gray-600 dark:text-gray-400">
          {displayCount} / {effectiveLimit} 笔记
        </span>
        {/* ★ 数据库容量提示：常态静态显示总库空间（用户感知为条数，不动态显示剩余容量）；
            ≥ 限额 95% 报警（琥珀）、≥ 限额已限制写入（红）；限额随套餐（免费 0.5M/会员 20M） */}
        {dbQuota.over ? (
          <span className="text-red-500 font-medium">库容量已满，已限制写入</span>
        ) : dbQuota.warn ? (
          <span className="text-yellow-600 dark:text-yellow-400">库容量已超{dbQuota.warnShort}</span>
        ) : (
          <span className="text-gray-400">{dbQuota.limitDisplay}</span>
        )}
      </div>

      {dbQuota.over && (
        <div className="mt-2 p-2 bg-red-50 dark:bg-red-900/20 rounded text-xs text-red-600 dark:text-red-400">
          数据库总空间已满（{dbQuota.limitShort}），已限制保存新内容。请删除笔记或清空回收站释放空间
        </div>
      )}

      {dbQuota.warn && !dbQuota.over && (
        <div className="mt-2 p-2 bg-yellow-50 dark:bg-yellow-900/20 rounded text-xs text-yellow-600 dark:text-yellow-400">
          数据库容量已超过 {dbQuota.warnShort}（上限 {dbQuota.limitShort}），建议清理笔记或清空回收站
        </div>
      )}

      {isExceeded && (
        <div className="mt-2 p-2 bg-red-50 dark:bg-red-900/20 rounded text-xs text-red-600 dark:text-red-400">
          笔记数量已达上限，请升级会员以创建更多笔记
        </div>
      )}

      {isNearLimit && !isExceeded && (
        <div className="mt-2 p-2 bg-yellow-50 dark:bg-yellow-900/20 rounded text-xs text-yellow-600 dark:text-yellow-400">
          笔记数量即将达到上限，建议升级会员
        </div>
      )}

      <div className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-700">
        <div className="flex items-center justify-between mb-1">
          <span className="text-xs font-medium text-gray-500 dark:text-gray-400">
            存储空间
          </span>
          <span className="text-xs text-gray-400 dark:text-gray-500">
            {formatStorageSize(storageData.used)} / {formatStorageSize(storageData.limit)}
          </span>
        </div>

        <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2 mb-1">
          <div
            className={`h-2 rounded-full transition-all duration-500 ${getStorageProgressBarColor()}`}
            style={{ width: `${Math.min(100, storageData.percentage)}%` }}
          ></div>
        </div>

        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1">
            <button
              onClick={() => setShowAttachmentManager(true)}
              className="text-xs text-blue-500 hover:text-blue-600 dark:text-blue-400 font-medium transition-colors"
            >
              附件
            </button>
            <span className="text-gray-300 dark:text-gray-600">|</span>
            <button
              onClick={() => setShowImageManager(true)}
              className="text-xs text-blue-500 hover:text-blue-600 dark:text-blue-400 font-medium transition-colors"
            >
              图片
            </button>
            <button
              onClick={refreshStorage}
              className="p-0.5 rounded hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              title="刷新存储空间"
            >
              <svg className="w-3 h-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
            </button>
          </div>
          <span className="text-xs text-gray-400 dark:text-gray-500">
            剩余 {formatStorageSize(Math.max(0, storageData.limit - storageData.used))}
          </span>
        </div>
      </div>

      <OverlayModal
        open={showAttachmentManager}
        title="附件管理"
        onClose={() => {
          setShowAttachmentManager(false)
          refreshStorage()
        }}
        maxWidth={800}
        showMobileCloseButton
      >
        <Suspense fallback={null}>
          <AttachmentManager />
        </Suspense>
      </OverlayModal>

      <OverlayModal
        open={showImageManager}
        title="图片管理"
        onClose={() => {
          setShowImageManager(false)
          refreshStorage()
        }}
        maxWidth={900}
        showMobileCloseButton
      >
        <Suspense fallback={null}>
          <ImageManager />
        </Suspense>
      </OverlayModal>
    </div>
  )
}

export default QuotaDisplay
